import React, { useEffect, useState, useRef, useCallback } from 'react';
import Api from "../../../services/api"; 
import moment from 'moment';
import {
    Box,
    SimpleGrid,
    Heading,
    Text,
    useTheme,
    Tooltip as ChakraTooltip, 
    Flex,
    VStack,
    Spinner,
    Icon
} from '@chakra-ui/react';

import StatusCard from '../../../components/UI/StatusCard'; 
import TopErrorsTable from '../../../components/charts/TopErrorsTable'; 
import { Loading } from '../../../components/loading'; 
import CustomTooltip from '../../../components/UI/CustomTooltip'; 
import CustomBarCursor from '../../../components/UI/CustomBarCursor'; 
import DashboardHeader from '../../../components/Layout/DashboardHeader'; 

import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    BarChart,
    Bar,
    CartesianGrid
} from 'recharts';

import {
    AiOutlineClockCircle,
    AiOutlineSchedule,
    AiOutlineCalendar,
} from 'react-icons/ai';

const PanelErp = () => {
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true); 
    const [isFiltering, setIsFiltering] = useState(false); 

    const defaultStartDate = moment().subtract(29, 'days').format('YYYY-MM-DD');
    const defaultEndDate = moment().format('YYYY-MM-DD');

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    const controllerRef = useRef(null);

    const fetchData = useCallback(async (options = {}) => {
        const { 
            triggeredByFilter = false, 
            fetchStartDate = startDate, 
            fetchEndDate = endDate      
        } = options;

        if (triggeredByFilter) {
            setIsFiltering(true);
        } else if (!data) { 
            setLoading(true);
        }

        console.log(`[LOG - FETCH] Buscando dados. Datas a serem usadas: ${fetchStartDate} a ${fetchEndDate}`);

        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        const { signal } = controllerRef.current;

        const apiStartDate = moment(fetchStartDate).format('YYYY-MM-DD'); 
        const apiEndDate = moment(fetchEndDate).format('YYYY-MM-DD');   

        const params = { startDate: apiStartDate, endDate: apiEndDate };
        console.log("[LOG - API] Enviando params:", params);

        try {
            const res = await Api.get('/monitorerp/dashboarddata', { params, signal });
            if (!signal.aborted) {
                console.log("[LOG - API] Dados recebidos:", res.data);
                setData(res.data); 
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Erro ao carregar dados:', error);
                if (!signal.aborted) setData(null);
            }
        } finally {
            if (!signal.aborted) {
                setLoading(false);
                setIsFiltering(false);
            }
        }
    }, [startDate, endDate, data]); 

    const handleFilter = () => {
        console.log("[LOG - FILTER] Clicado. Datas atuais:", startDate, endDate);
        if (!startDate || !endDate) { alert("Selecione datas."); return; }
        if (new Date(startDate) > new Date(endDate)) { alert("Data inicial maior que final."); return; }
        fetchData({ triggeredByFilter: true }); 
    };

    const handleClearFilter = useCallback(() => {
        console.log("[LOG - CLEAR FILTER] Limpando filtro para o padrão.");
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        fetchData({ 
            triggeredByFilter: true, 
            fetchStartDate: defaultStartDate, 
            fetchEndDate: defaultEndDate 
        }); 
    }, [fetchData, defaultStartDate, defaultEndDate]);


    useEffect(() => {
        console.log("[LOG - INIT] Montando componente e buscando dados iniciais.");
        fetchData({ fetchStartDate: defaultStartDate, fetchEndDate: defaultEndDate });
        
        return () => {
            console.log("[LOG - CLEANUP] Desmontando componente, abortando fetch se houver.");
            controllerRef.current?.abort();
        }
    }, []); 


    if (loading) return <Loading />; 
    if (!loading && (!data || data.error)) return ( 
        <Flex justify="center" align="center" h="80vh" p={6}>
            <Text color="gray.400">{data?.error || 'Sem dados para exibir ou erro ao carregar.'}</Text>
        </Flex>
    );
    if (!data) return <Loading />;

    const {
        period = { startDate: 'N/D', endDate: 'N/D' },
        cards = {},
        charts = { errorsPerHour: [], errorsPerDay: [] },
        distributions = { errorTypes: [] },
        grids = { topErrors: [] }
    } = data || {}; 

    const totalErrorsMonth = cards.totalErrorsMonth || 0;
    const totalErrorsLastMonth = cards.totalErrorsLastMonth || 0;
    const totalErrorsWeek = cards.totalErrorsWeek || 0;
    const totalErrorsLastWeek = cards.totalErrorsLastWeek || 0;
    const totalErrorsToday = cards.totalErrorsToday || 0;
    const totalErrorsYesterday = cards.totalErrorsYesterday || 0;

    const percentMonth = ((totalErrorsMonth - totalErrorsLastMonth) / (totalErrorsLastMonth || 1)) * 100;
    const percentWeek = ((totalErrorsWeek - totalErrorsLastWeek) / (totalErrorsLastWeek || 1)) * 100;
    const percentDay = ((totalErrorsToday - totalErrorsYesterday) / (totalErrorsYesterday || 1)) * 100;

    const isPositiveMonth = totalErrorsMonth >= totalErrorsLastMonth; 
    const isPositiveWeek = totalErrorsWeek >= totalErrorsLastWeek;
    const isPositiveDay = totalErrorsToday >= totalErrorsYesterday;

    const chartHours = charts.errorsPerHour.map(e => ({ name: `${String(e._id?.hour ?? '??').padStart(2, '0')}:00`, 'Erros': e.count || 0 }));
    const chartDays = charts.errorsPerDay.map(e => ({ name: `${String(e._id?.day ?? '??').padStart(2, '0')}/${String(e._id?.month ?? '??').padStart(2, '0')}`, 'Erros': e.count || 0 }));

    const errorsByType = distributions.errorTypes
        .filter(e => e._id)
        .map(e => ({ name: e._id, errors: e.count || 0 }))
        .sort((a, b) => a.errors - b.errors);
        
    const topErrorsData = grids.topErrors || [];

    const periodText = `Dados do período: ${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`;

    const truncateTick = (tick) => {
        if (typeof tick !== 'string') return tick;
        return tick.length > 20 ? tick.substring(0, 20) + '...' : tick; 
    };

    const barChartHeight = Math.max(350, errorsByType.length * 40);

    return (
        <Box p="6" bg="background" color="white" minH="100vh">

            <DashboardHeader
                title="Dashboard de Erros ERP"
                startDate={startDate} 
                setStartDate={setStartDate} 
                endDate={endDate} 
                setEndDate={setEndDate} 
                onFilterClick={handleFilter} 
                periodText={periodText}
                isDisabled={isFiltering} 
                onClearFilter={handleClearFilter} 
            />

            <SimpleGrid columns={[1, 2, 2, 3]} spacing="6" mb="8">
                <ChakraTooltip label={`Comparativo com período anterior (${totalErrorsLastMonth.toLocaleString('pt-BR')})`} bg="gray.700" color="white" borderRadius="md">
                    <Box>
                        <StatusCard
                            icon={AiOutlineSchedule}
                            title="Total no Período"
                            value={totalErrorsMonth.toLocaleString('pt-BR')}
                            change={`${Math.abs(percentMonth).toFixed(1)}%`}
                            isPositive={isPositiveMonth} 
                            colorScheme={'green'} 
                        />
                    </Box>
                </ChakraTooltip>
                <ChakraTooltip label={`Últimos 7 dias do filtro. Comp. com 7 dias anteriores (${totalErrorsLastWeek.toLocaleString('pt-BR')})`} bg="gray.700" color="white" borderRadius="md">
                    <Box>
                        <StatusCard
                            icon={AiOutlineCalendar}
                            title="Erros (Últimos 7d)"
                            value={totalErrorsWeek.toLocaleString('pt-BR')}
                            change={`${Math.abs(percentWeek).toFixed(1)}%`}
                            isPositive={isPositiveWeek} 
                            colorScheme={'green'} 
                        />
                    </Box>
                </ChakraTooltip>
                <ChakraTooltip label={`Último dia do filtro. Comp. com dia anterior (${totalErrorsYesterday.toLocaleString('pt-BR')})`} bg="gray.700" color="white" borderRadius="md">
                    <Box>
                        <StatusCard
                            icon={AiOutlineClockCircle}
                            title="Erros (Último Dia)"
                            value={totalErrorsToday.toLocaleString('pt-BR')}
                            change={`${Math.abs(percentDay).toFixed(1)}%`}
                            isPositive={isPositiveDay} 
                            colorScheme={'green'} 
                        />
                    </Box>
                </ChakraTooltip>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={8}>
                <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                    <Heading as="h3" size="md" color="white" mb={4}>Erros nas Últimas 24 Horas</Heading>
                        {chartHours.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartHours}>
                                    <defs><linearGradient id="gGreenHr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={theme.colors.secondary} stopOpacity={0.4}/><stop offset="95%" stopColor={theme.colors.secondary} stopOpacity={0}/></linearGradient></defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                    <XAxis dataKey="name" stroke={theme.colors.gray[500]} tick={{ fontSize: 12 }} />
                                    <YAxis stroke={theme.colors.gray[500]} tick={{ fontSize: 12 }} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                                    <Area type="monotone" dataKey="Erros" stroke={theme.colors.secondary} fill="url(#gGreenHr)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : ( <Flex justify="center" align="center" h="300px"><Text color="gray.500">Sem dados por hora.</Text></Flex> )}
                </Box>
                <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                    <Heading as="h3" size="md" color="white" mb={4}>Tendência de Erros (Últimos 15d do Filtro)</Heading>
                    {chartDays.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={chartDays}>
                                <defs><linearGradient id="gGreenDay" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={theme.colors.secondary} stopOpacity={0.4}/><stop offset="95%" stopColor={theme.colors.secondary} stopOpacity={0}/></linearGradient></defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                <XAxis dataKey="name" stroke={theme.colors.gray[500]} tick={{ fontSize: 12 }} interval="preserveStartEnd"/>
                                <YAxis stroke={theme.colors.gray[500]} tick={{ fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                                <Area type="monotone" dataKey="Erros" stroke={theme.colors.secondary} fill="url(#gGreenDay)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : ( <Flex justify="center" align="center" h="300px"><Text color="gray.500">Sem dados por dia.</Text></Flex> )}
                </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 1 }} spacing={6} mb={8}>
                
                <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800" h={barChartHeight}>
                    <Heading as="h3" size="sm" color="white" mb={4}>Distribuição por Tipo de Erro</Heading>
                    {errorsByType.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart 
                                data={errorsByType} 
                                layout="vertical" 
                                margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
                                barSize={20}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                <XAxis type="number" stroke={theme.colors.gray[500]} tickFormatter={(value) => value.toLocaleString('pt-BR')} tick={{ fill: theme.colors.gray[500], fontSize: 10 }} />
                                <YAxis 
                                    dataKey="name" 
                                    type="category" 
                                    stroke={theme.colors.gray[500]} 
                                    tick={{ fill: theme.colors.gray[500], fontSize: 10 }}
                                    interval={0} 
                                    tickFormatter={truncateTick} 
                                    width={150} 
                                />
                                <Tooltip content={<CustomTooltip />} cursor={<CustomBarCursor theme={theme} />} />
                                <Bar dataKey="errors" name="Total de Erros" fill={theme.colors.secondary} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : ( <Flex justify="center" align="center" h="300px"><Text color="gray.500">Sem dados de distribuição para exibir.</Text></Flex> )}
                </Box>
                
                <TopErrorsTable data={topErrorsData} />

            </SimpleGrid>
        </Box>
    );
};

export default PanelErp;